import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    withStyles,
    Grid,
} from '@material-ui/core';
import { AddBox, IndeterminateCheckBox, Add } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styles from './styles';
import PropTypes from 'prop-types';
import Table from '../../../../common/Table';
import {
    activeButtonsCell,
    deactivatedButtonsCell,
    tableColumn,
} from '../constants';
import { noop } from '../../../../common/utils';
import { getAppImages } from '../../../../utils';

import ActionDuplicationModal from '../../../../common/ActionDuplicationModal';
import {
    activeCommunityPropType,
    availableCommunitiesPropType,
} from '../../../../user/propTypes';
import Activate from '../../../../common/ActivateModal/Activate';
import {
    PIN_ADMIN_VISIBILITIES,
    PIN_ADMIN_VISIBILITY_INCLUDED_FOR_THEME,
} from '../../../../actions/constants';
import { actionTablePropType } from '../../../../actions/propTypes';
import CustomButton from '../../../../common/Button';
import { ReactComponent as BlankChallengeIcon } from '../../../../assets/images/svg/blank-challenge-icon.svg';
import { ReactComponent as MarketplaceIcon } from '../../../../assets/images/svg/shopping-cart.svg';
import {
    getFeatureFlagValue,
    REMOTE_CONFIG_SHOW_CUSTOM_ACTION_BUTTON,
} from '../../../../utils/remoteConfig';

const TableManager = ({
    classes,
    type,
    title,
    data,
    filter,
    onChangeActive,
    columns,
    shouldShowMultiSelect,
    actionDuplicationCommunities,
    activeCommunity,
    fetchDataFunc,
    buttonsCell,
    onButtonsCell,
    refetchData,
    userPermissions,
    activeCommunityId,
    theme,
    ...tableProps
}) => {
    const { t } = useTranslation();
    const history = useHistory();

    const [selectedRowIds, setSelectedRowIds] = useState({});
    const [rowsData, setRowsData] = useState({});
    const [openDialog, setOpenDialog] = useState(false);

    const [pagination, setPagination] = useState({
        skip: 0,
        limit: 10,
        sortBy: 'name',
        order: 1,
    });

    const tableData = useMemo(() => {
        const filteredData = data.data;
        return (
            filteredData?.map((item) =>
                [...columns, { accessor: 'id' }].reduce((row, cell) => {
                    return { ...row, [cell.accessor]: item[cell.accessor] };
                }, {}),
            ) || []
        );
    }, [columns, data.data]);

    const changeActivate = useCallback(
        (id) => {
            const currRows = !id
                ? selectedRowIds.reduce((list, itemId) => {
                      const item = tableData.find((item) => item.id === itemId);
                      return item ? [...list, item] : list;
                  }, [])
                : tableData.filter((item) => item.id === id);

            if (!currRows.length) return;

            setRowsData({
                isOpen: true,
                items: currRows.map(({ id, name, iconUrl }) => ({
                    name,
                    iconUrl: iconUrl,
                    identifier: id,
                })),
            });
        },
        [tableData, selectedRowIds],
    );

    const handleActions = useCallback(
        (id, key) => {
            switch (key) {
                case 'viewActionsAdmin':
                    history.push(`/actions/${id}`);
                    break;

                case 'editActions':
                    history.push(`/actions/${id}/edit`);
                    break;

                case 'deactivateActionExclude':
                case 'activateActionExclude':
                    changeActivate(id);
                    break;

                default:
                    alert({ id, key });
            }
        },
        [history, changeActivate],
    );

    const isIncluded = useMemo(
        () => type === PIN_ADMIN_VISIBILITY_INCLUDED_FOR_THEME,
        [type],
    );

    const renderHeadButtons = useCallback(() => {
        const disabled = Object.keys(selectedRowIds).length === 0;
        return (
            <IconButton onClick={() => changeActivate()} disabled={disabled}>
                <Tooltip
                    placement="top"
                    title={isIncluded ? t('deactivate') : t('activate')}
                >
                    {isIncluded ? <IndeterminateCheckBox /> : <AddBox />}
                </Tooltip>
            </IconButton>
        );
    }, [t, selectedRowIds, isIncluded, changeActivate]);

    const action = isIncluded ? t('deactivate') : t('activate');
    const descriptionTransKey = isIncluded
        ? 'action.deactivate.description'
        : 'action.activate.description';
    const description = t(descriptionTransKey, {
        count: rowsData.items?.length,
    });

    const handleConfirmToggleActivate = (communityIds) => {
        onChangeActive(
            rowsData.items.map(({ identifier }) => identifier),
            communityIds,
            {
                visibility: type,
                ...pagination,
            },
        );
        setRowsData({});
    };

    const handleFetchData = useCallback(
        (args) => {
            setPagination(args);
            const body = {
                ...args,
                visibility: type,
                search: filter,
            };
            fetchDataFunc(body);
        },
        [fetchDataFunc, type, filter],
    );

    const refetchDataFunc = useCallback(() => {
        fetchDataFunc({
            ...pagination,
            visibility: type,
        });
    }, [fetchDataFunc, pagination, type]);

    useEffect(() => {
        refetchDataFunc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchData]);

    const handleAddAction = () => {
        handleCreateCustomAction(theme);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleCreateCustomAction = (selectedTheme) => {
        history.push(
            `/actions/create?communityId=${activeCommunityId}&themeId=${selectedTheme.id}`,
        );
        handleCloseDialog();
    };

    const renderAddButton = () => (
        <CustomButton
            color="primary"
            onClick={handleAddAction}
            style={{ borderRadius: '10px', marginLeft: '16px' }}
            data-intercom-target="Actions Add Button"
        >
            <Add />
            {t('actions.add')}
        </CustomButton>
    );

    const showCustomActionButton = getFeatureFlagValue(
        REMOTE_CONFIG_SHOW_CUSTOM_ACTION_BUTTON,
    );

    return (
        <Fragment>
            <Table
                title={title}
                columns={columns}
                data={tableData}
                multiActionButton={shouldShowMultiSelect && renderHeadButtons()}
                buttonsCell={
                    buttonsCell ||
                    (isIncluded ? activeButtonsCell : deactivatedButtonsCell)
                }
                onButtonsCell={onButtonsCell || handleActions}
                onRowSelect={setSelectedRowIds}
                fetchDataFunc={handleFetchData}
                searchString={filter}
                totalCount={data.pagination?.totalCount}
                loading={data.loading}
                updatePaginationOptions={setPagination}
                paginationOptions={pagination}
                activeCommunityId={activeCommunity.id}
                renderExtraControls={
                    title === t('active') && showCustomActionButton
                        ? renderAddButton
                        : null
                }
                {...tableProps}
            />
            {openDialog && (
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    className={classes.dialog}
                >
                    <DialogTitle className={classes.dialogTitle}>
                        {t('actions.add')}
                        <IconButton
                            aria-label="close"
                            className={classes.closeButton}
                            onClick={handleCloseDialog}
                        >
                            <img
                                src={getAppImages().closeIcon}
                                alt="Close"
                                width="14"
                                height="14"
                            />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className={classes.dialogContent}>
                        <Grid container spacing={2}>
                            {showCustomActionButton && (
                                <Grid item xs={12} sm={6}>
                                    <Button
                                        onClick={() =>
                                            handleCreateCustomAction(theme)
                                        }
                                        className={classes.dialogButton}
                                        fullWidth
                                    >
                                        <div className={classes.buttonContent}>
                                            <BlankChallengeIcon
                                                className={classes.icon}
                                            />
                                            <span
                                                className={classes.buttonText}
                                            >
                                                {t(
                                                    'actions.create.customAction',
                                                )}
                                            </span>
                                        </div>
                                    </Button>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Button
                                    onClick={() =>
                                        history.push('actions/marketplace')
                                    }
                                    className={classes.dialogButton}
                                    fullWidth
                                >
                                    <div className={classes.buttonContent}>
                                        <MarketplaceIcon
                                            className={classes.icon}
                                        />
                                        <span className={classes.buttonText}>
                                            {t('actions.searchMarketplace')}
                                        </span>
                                    </div>
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Dialog>
            )}
            <ActionDuplicationModal
                isOpen={rowsData.isOpen || false}
                onClose={() => setRowsData({})}
                onCancel={() => setRowsData({})}
                onConfirm={(communityIds) => {
                    handleConfirmToggleActivate(communityIds);
                }}
                actionDuplicationCommunities={actionDuplicationCommunities}
                activeCommunity={activeCommunity}
                title={t('confirmUpdate')}
                confirmBtnType={isIncluded ? 'danger' : 'success'}
                confirmBtnText={action}
            >
                <Activate
                    isActivate={!isIncluded}
                    action={!isIncluded ? t('activate') : t('deactivate')}
                    headerKey="action.areYouSure"
                    items={rowsData.items || []}
                    description={description}
                />
            </ActionDuplicationModal>
        </Fragment>
    );
};

export default withStyles(styles)(TableManager);

TableManager.propTypes = {
    type: PropTypes.oneOf(PIN_ADMIN_VISIBILITIES),
    title: PropTypes.string,
    data: actionTablePropType,
    filter: PropTypes.string,
    onChangeActive: PropTypes.func,
    columns: PropTypes.object,
    shouldShowMultiSelect: PropTypes.bool,
    actionDuplicationCommunities: availableCommunitiesPropType,
    activeCommunity: activeCommunityPropType,
    fetchDataFunc: PropTypes.func,
    buttonsCell: PropTypes.object,
    onButtonsCell: PropTypes.func,
    refetchData: PropTypes.number,
    classes: PropTypes.object,
    userPermissions: PropTypes.object,
    activeCommunityId: PropTypes.string,
    theme: PropTypes.object,
};

TableManager.defaultProps = {
    type: PIN_ADMIN_VISIBILITY_INCLUDED_FOR_THEME,
    filter: '',
    onChangeActive: noop,
    title: null,
    columns: tableColumn,
    shouldShowMultiSelect: true,
    actionDuplicationCommunities: [],
    activeCommunity: {},
    fetchDataFunc: noop,
    buttonsCell: null,
    onButtonsCell: null,
    data: {},
    classes: {},
    userPermissions: {},
    activeCommunityId: '',
    refetchData: 0,
    theme: {},
};
